/**
 * EnterpriseReports — Phase 8 dashboard.
 *
 * One-page hub for generating and consuming the three enterprise PDF products:
 *   • ESG Sustainability Report
 *   • CBAM Exposure Report
 *   • Executive Summary (one-pager)
 *
 * RBAC: admin/editor can generate; auditor can view + download; supplier is
 * blocked by RoleRoute upstream. Downloads pull the PDF via axios blob and
 * trigger a synthetic <a download> click.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Leaf, Factory, FileText, Loader2, Download, RefreshCw, ShieldCheck, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const TYPES = [
  { id: 'esg',       label: 'ESG Sustainability Report',
    icon: Leaf, colour: 'from-emerald-700 to-emerald-800',
    desc: 'Multi-page report covering products, evidence, suppliers and CBAM exposure.' },
  { id: 'cbam',      label: 'CBAM Exposure Report',
    icon: Factory, colour: 'from-red-700 to-orange-700',
    desc: 'Deep border-tax analysis with per-product breakdown and recommendations.' },
  { id: 'executive', label: 'Executive Summary',
    icon: FileText, colour: 'from-indigo-700 to-purple-700',
    desc: 'One-page CEO / CFO briefing — top priorities and highlights.' },
];

const TYPE_BADGE = {
  esg:       'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
  cbam:      'bg-red-500/10 text-red-700 border-red-500/30',
  executive: 'bg-indigo-500/10 text-indigo-700 border-indigo-500/30',
};

function fmtBytes(b) {
  const n = Number(b || 0);
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024)        return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}

export default function EnterpriseReports() {
  const { user } = useAuth();
  const role = (user?.role || 'user').toLowerCase();
  const canGenerate = role === 'admin' || role === 'editor';

  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({ esg: false, cbam: false, executive: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/enterprise-reports', { params: { limit: 50 } });
      setReports(r.data?.reports || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load reports');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async (type) => {
    setBusy((b) => ({ ...b, [type]: true }));
    try {
      const r = await api.post(`/enterprise-reports/generate/${type}`);
      toast.success(`${type.toUpperCase()} report ready · ${fmtBytes(r.data?.file_size)}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to generate ${type} report`);
    } finally { setBusy((b) => ({ ...b, [type]: false })); }
  };

  const download = async (rep) => {
    try {
      const r = await api.get(`/enterprise-reports/${rep.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${rep.report_type}_${rep.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Download failed');
    }
  };

  const byType = useMemo(() => {
    const g = { esg: 0, cbam: 0, executive: 0 };
    reports.forEach((r) => { g[r.report_type] = (g[r.report_type] || 0) + 1; });
    return g;
  }, [reports]);

  return (
    <div className="p-6 pb-32 max-w-7xl mx-auto space-y-6" data-testid="enterprise-reports-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-600" />
            <h1 className="text-2xl font-heading font-bold" data-testid="enterprise-reports-title">
              Enterprise Reporting Suite
            </h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Audit-ready PDFs · cryptographically hashed · QR-verifiable
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!canGenerate && (
            <Badge variant="outline" className="text-xs" data-testid="enterprise-reports-viewer-badge">
              <ShieldCheck className="w-3 h-3 mr-1" /> Viewer role
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={load} data-testid="enterprise-reports-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Generation cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {TYPES.map((t) => {
          const Icon = t.icon;
          return (
            <Card key={t.id} className="p-0 overflow-hidden" data-testid={`enterprise-report-card-${t.id}`}>
              <div className={`bg-gradient-to-br ${t.colour} text-white p-5`}>
                <div className="flex items-center justify-between">
                  <Icon className="w-8 h-8" />
                  <Badge variant="secondary" className="text-xs">
                    {byType[t.id] || 0} generated
                  </Badge>
                </div>
                <div className="font-heading font-semibold text-base mt-3">{t.label}</div>
                <div className="text-xs opacity-90 mt-1">{t.desc}</div>
              </div>
              <div className="p-4">
                <Button
                  className="w-full"
                  disabled={!canGenerate || busy[t.id]}
                  onClick={() => generate(t.id)}
                  data-testid={`enterprise-report-generate-${t.id}`}
                >
                  {busy[t.id]
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
                    : canGenerate ? 'Generate now' : 'Read-only'}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Reports list */}
      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div>
            <div className="font-heading font-semibold text-sm">Report history</div>
            <div className="text-[11px] text-muted-foreground">
              {loading ? 'Loading…' : `${reports.length} of ${total} shown · newest first`}
            </div>
          </div>
        </div>
        {loading ? (
          <div className="py-8 text-center text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading history…
          </div>
        ) : reports.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="enterprise-reports-empty">
            No reports yet. {canGenerate ? 'Generate your first report above.' : 'Ask your admin to generate one.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="enterprise-reports-table">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium text-xs uppercase tracking-wide text-muted-foreground">Type</th>
                  <th className="px-4 py-2 font-medium text-xs uppercase tracking-wide text-muted-foreground">Title</th>
                  <th className="px-4 py-2 font-medium text-xs uppercase tracking-wide text-muted-foreground">Generated</th>
                  <th className="px-4 py-2 font-medium text-xs uppercase tracking-wide text-muted-foreground">By</th>
                  <th className="px-4 py-2 font-medium text-xs uppercase tracking-wide text-muted-foreground">Size</th>
                  <th className="px-4 py-2 font-medium text-xs uppercase tracking-wide text-muted-foreground">Integrity</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {reports.map((r) => (
                  <tr key={r.id} className="hover:bg-muted/30" data-testid={`enterprise-report-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`text-[10px] ${TYPE_BADGE[r.report_type] || ''}`}>
                        {r.report_type.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 font-medium">{r.title}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {new Date(r.generated_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-xs">{r.created_by_email || r.created_by_name || '—'}</td>
                    <td className="px-4 py-3 text-xs font-mono">{fmtBytes(r.file_size)}</td>
                    <td className="px-4 py-3 text-[10px] font-mono text-muted-foreground" title={r.audit_hash}>
                      {r.audit_hash ? `${r.audit_hash.slice(0, 12)}…` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => download(r)}
                        data-testid={`enterprise-report-download-${r.id}`}
                      >
                        <Download className="w-3.5 h-3.5 mr-1" /> PDF
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
